
import React from 'react'
import WelcomeMessage from './WelcomeMessage';
import { EmailInfo, useTabStore } from '../../providers/TabProvider';
import { useMutation } from '@tanstack/react-query';
import LoadingProgress from '../../components/LoadingProgress';
import { scanEmail } from '../../api';
import { useResultStore } from '../../providers/ResultProvider';
import { ScanRecord, useScanHistoryStore } from '../../providers/ScanHistoryProvider';

import { MessageBar, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';

export default function HeroScanSection() {
  const emailData = useTabStore((s) => s.emailData);
  const { setActiveTab } = useTabStore();
  const setInsightData = useResultStore((s) => s.setInsightData);
  const setDeepScanData = useResultStore((s) => s.setDeepScanData);
  const setIndicatorStatus = useResultStore((s) => s.setIndicatorStatus);

  const [scanError, setScanError] = React.useState<string | null>(null);

  // Scan history
  const hasScanRecord = useScanHistoryStore((s) => s.hasScanRecord);
  const getScanRecord = useScanHistoryStore((s) => s.getScanRecord);
  const getScannedAt = useScanHistoryStore((s) => s.getScannedAt);
  const addScanRecord = useScanHistoryStore((s) => s.addScanRecord);

  const fingerprint = emailData?.emailFingerprint;

  // Check if this email was already scanned
  // const alreadyScanned = fingerprint ? hasScanRecord(fingerprint) : false;

  // ! Temporary for false positive
  const alreadyScanned = false;
  const previousScanRecord = fingerprint ? getScanRecord(fingerprint) : undefined;
  const scannedAt = fingerprint ? getScannedAt(fingerprint) : null;

  /**
   * Load cached results from a previous scan into the result stores and navigate.
   */
  const handleViewPreviousResults = () => {
    if (!previousScanRecord) return;

    if (previousScanRecord.insightData && Object.keys(previousScanRecord.insightData).length > 0) {
      setInsightData(previousScanRecord.insightData as any);
    }
    if (previousScanRecord.deepScanData && Object.keys(previousScanRecord.deepScanData).length > 0) {
      setDeepScanData(previousScanRecord.deepScanData as any);
    }
    if (previousScanRecord.indicatorStatus) {
      setIndicatorStatus(previousScanRecord.indicatorStatus);
    }
    setActiveTab("ai-insight-result");
  };

  const { mutate, isPending } = useMutation({
    mutationFn: async (data: Partial<EmailInfo>) => {
      setScanError(null); // Clear previous errors
      return await scanEmail({ emailData: data });
    },
    onSuccess: (data) => {
      console.log(data, "Data from API - inside onSuccess");

      if (data && data.finder_id) {
        setActiveTab("ai-insight-result");
        setInsightData(data);

        // Save scan record for deduplication
        if (fingerprint) {
          addScanRecord({
            fingerprint,
            internetMessageId: emailData?.internetMessageId,
            scannedAt: new Date().toISOString(),
            insightData: data,
          });
        }
      } else {
        console.error("Invalid data or error received:", data);
        let errorMessage = "An unexpected error occurred.";

        if (data?.response?.status === 401 || data?.status === 401) {
          errorMessage = "Authentication failed. Please check your login session.";
        } else if (data?.response?.data?.detail) {
          errorMessage = data.response.data.detail;
        } else if (data?.message) {
          errorMessage = data.message;
        }

        setScanError(errorMessage);
      }
    },
    onError: (error) => {
      console.error("Error during loading:", error);
      setScanError(error.message || "Failed to scan email.");
    }
  });

  if (isPending) return <LoadingProgress />

  return (
    <>
      {scanError && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Scan Failed</MessageBarTitle>
            {scanError}
          </MessageBarBody>
        </MessageBar>
      )}

      {!isPending && (
        <WelcomeMessage
          handleInfo={(data) => {
            mutate(data);
          }}
          alreadyScanned={alreadyScanned}
          scannedAt={scannedAt}
          onViewPreviousResults={handleViewPreviousResults}
        />
      )}
    </>
  )
}
